/**
 * Hook to resolve translated app name and description.
 *
 * Resolution priority (per field):
 *   1. DB translation — via useEntityTranslation (admin-editable, highest priority)
 *   2. JSON locale file — keyed by appCode in the "appDescriptions" namespace
 *   3. Raw DB fallback — the value stored directly in the SysApp record
 *
 * DB-first approach allows admins to override any static locale JSON translation
 * at runtime without code changes.
 */

import { useMessages } from 'next-intl';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useEntityTranslation } from '@/app/hooks/useEntityTranslation';
import { useCallback } from 'react';

interface AppText {
  name: string;
  description: string;
}

export function useAppDescription() {
  const { t } = useTranslation('appDescriptions');
  const messages = useMessages();
  const { getTranslation } = useEntityTranslation();

  // Check if a key exists in the appDescriptions namespace
  const hasKey = useCallback(
    (appCode: string) => {
      const ns = (messages as Record<string, unknown>)?.appDescriptions;
      return ns != null && typeof ns === 'object' && appCode in ns;
    },
    [messages]
  );

  const getAppText = useCallback(
    (
      appCode: string,
      fallbackName?: string,
      fallbackDescription?: string | null,
      entityId?: number
    ): AppText => {
      // Priority 1: DB translation (admin-editable override)
      if (entityId != null) {
        const dbName = getTranslation('sysapp', entityId, 'app_name', '');
        const dbDesc = getTranslation('sysapp', entityId, 'description', '');
        // Only use DB values if they actually exist (non-empty)
        if (dbName || dbDesc) {
          return {
            name: dbName || (hasKey(appCode) ? t(`${appCode}.name` as any) : '') || fallbackName || appCode,
            description: dbDesc || (hasKey(appCode) ? t(`${appCode}.description` as any) : '') || fallbackDescription || '',
          };
        }
      }

      // Priority 2: JSON locale file
      if (hasKey(appCode)) {
        return {
          name: t(`${appCode}.name` as any) || fallbackName || appCode,
          description: t(`${appCode}.description` as any) || fallbackDescription || '',
        };
      }

      // Priority 3: Raw DB fallback
      return {
        name: fallbackName ?? appCode,
        description: fallbackDescription ?? '',
      };
    },
    [t, hasKey, getTranslation]
  );

  return { getAppText };
}
